// app/components/AdvancedChart/hooks/useChartActions.ts
import { useCallback, useState } from 'react'
import { UTCTimestamp, LineSeries, Time } from 'lightweight-charts'
import React from 'react' // For proper type definitions
import { toUTCTimestamp } from '../utils/time'

interface UseChartActionsProps {
  price: number | null
  // Reference to the main chart for drawing a line series.
  mainChartRef: React.MutableRefObject<any>
  // The current chart data array.
  chartData: any[]
  // Setter function to toggle action visibility.
  setActionsVisible: React.Dispatch<React.SetStateAction<boolean>>
  // Current symbol being displayed on the chart
  currentSymbol: string
}

/**
 * A custom hook for handling action button events:
 * - Toggling the actions panel.
 * - Executing the "Buy", "Sell", and "Draw" actions.
 *
 * The "Buy" and "Sell" actions open a trading modal with the appropriate connector.
 * The "Draw" action creates a horizontal line at the given price across the chart.
 */
export function useChartActions({
  price,
  mainChartRef,
  chartData,
  setActionsVisible,
  currentSymbol
}: UseChartActionsProps) {
  // State for trading modal
  const [isTradingModalOpen, setIsTradingModalOpen] = useState(false)
  const [tradingAction, setTradingAction] = useState<'BUY' | 'SELL' | null>(null)
  // Toggle the actions panel.
  const toggleActions = useCallback(() => {
    setActionsVisible(prev => !prev)
  }, [setActionsVisible])

  // Handle the Buy action - open trading modal with BUY action.
  const handleBuy = useCallback(() => {
    if (price == null) return
    setTradingAction('BUY')
    setIsTradingModalOpen(true)
    setActionsVisible(false)
  }, [price, setActionsVisible])

  // Handle the Sell action - open trading modal with SELL action.
  const handleSell = useCallback(() => {
    if (price == null) return
    setTradingAction('SELL')
    setIsTradingModalOpen(true)
    setActionsVisible(false)
  }, [price, setActionsVisible])
  
  // Close the trading modal
  const handleCloseModal = useCallback(() => {
    setIsTradingModalOpen(false)
  }, [])

  // Handle the Draw action by adding a horizontal line across the main chart.
  const handleDraw = useCallback(() => {
    if (price == null || !mainChartRef.current || chartData.length === 0) return
    // Use the new v5 API: import LineSeries and call addSeries(LineSeries, options)
    const lineSeries = mainChartRef.current.addSeries(LineSeries, {
      color: '#88a1ac',
      lineWidth: 1
    })
    // Get the time bounds from the first and last candle in chartData.
    const firstTime = toUTCTimestamp(chartData[0].time as Time)!
    const lastTime = toUTCTimestamp(chartData[chartData.length - 1].time as Time)!
    lineSeries.setData([
      { time: firstTime, value: price },
      { time: lastTime, value: price }
    ])
    setActionsVisible(false)
  }, [price, mainChartRef, chartData, setActionsVisible])

  return { 
    toggleActions, 
    handleBuy, 
    handleSell, 
    handleDraw,
    isTradingModalOpen,
    handleCloseModal,
    tradingAction
  }
}
